import Axios from "axios";
import { ApiResponse, handleApiErrors } from "..";
import * as Models from "./models";
import {trAmount, trCard, trCardCountInt, trDate, trDateTime, trDateTimeSeconds, trInt} from "../../utils/tr"

const baseUrl = () => {
    return `${process.env.REACT_APP_MOBILBASE_API}/api/v1`;
}

export async function getDeviceList(location: number): Promise<ApiResponse<Models.DeviceListResponse>> {
    return Axios.get<Models.DeviceListResponse>(`${baseUrl()}/statistics/devices/${location}`)
        .then(res => { return {data: res.data, errorStatus: undefined, errorMessage: undefined}})
        .catch((e) => handleApiErrors<Models.DeviceListResponse>(e))
}

export async function getStatsCreditCardPaymentByMeter(params: Models.Params): Promise<ApiResponse<Models.PaymentResponse>> {
    return Axios.post<Models.PaymentResponse>(
        `${baseUrl()}/statistics/credit-card-payment-by-meter-and-card/${params.location}`,
        { ...params.body }
    ).then(res => {
        return {data: res.data, errorStatus: undefined, errorMessage: undefined}})
    .catch((e) => handleApiErrors<Models.PaymentResponse>(e))
}

export async function getStatsDebitCardPaymentByMeter(params: Models.Params): Promise<ApiResponse<Models.PaymentResponse>> {
    return Axios.post<Models.PaymentResponse>(
        `${baseUrl()}/statistics/debit-card-payment-by-meter-and-card/${params.location}`,
        { ...params.body }
    ).then(res => {
        return {data: res.data, errorStatus: undefined, errorMessage: undefined}})
    .catch((e) => handleApiErrors<Models.PaymentResponse>(e))
}

export async function getStatsCoinPaymentByMeter(params: Models.Params): Promise<ApiResponse<Models.PaymentResponse>> {
    return Axios.post<Models.PaymentResponse>(
        `${baseUrl()}/statistics/coin-payment-by-meter/${params.location}`,
        { ...params.body }
    ).then(res => {
        return {data: res.data, errorStatus: undefined, errorMessage: undefined}})
    .catch((e) => handleApiErrors<Models.PaymentResponse>(e))
}

export async function getStatsFeeByMeterAndCard(params: Models.Params): Promise<ApiResponse<Models.PaymentResponse>> {
    return Axios.post<Models.PaymentResponse>(
        `${baseUrl()}/statistics/fee-by-meter-and-card/${params.location}`,
        { ...params.body }
    ).then(res => {
        return {data: res.data, errorStatus: undefined, errorMessage: undefined}})
    .catch((e) => handleApiErrors<Models.PaymentResponse>(e))
}

export async function getStatsMobilePayPaymentByMeter(params: Models.Params): Promise<ApiResponse<Models.PaymentResponse>> {
    return Axios.post<Models.PaymentResponse>(
        `${baseUrl()}/statistics/mobilepay-payment-by-meter/${params.location}`,
        { ...params.body }
    ).then(res => {
        return {data: res.data, errorStatus: undefined, errorMessage: undefined}})
    .catch((e) => handleApiErrors<Models.PaymentResponse>(e))
}

export async function getEmptyingReport(params: Models.Params): Promise<ApiResponse<Models.EmptyingResponse>> {
    return Axios.post<Models.EmptyingResponse>(`${baseUrl()}/reports/emptying/${params.location}`, { ...params.body })
        .then(res => {
            res.data.result.forEach(row => {
                row.emptyNr = trInt(row.emptyNr)
                row.emptyTimeString = trDateTimeSeconds(row.emptyTime)
                row.amount = trAmount(row.amount)
                row.valueDk001 = trAmount(row.valueDk001)
                row.countDk001 = trInt(row.countDk001)
                row.valueDk002 = trAmount(row.valueDk002)
                row.countDk002 = trInt(row.countDk002)
                row.valueDk005 = trAmount(row.valueDk005)
                row.countDk005 = trInt(row.countDk005)
                row.valueDk010 = trAmount(row.valueDk010)
                row.countDk010 = trInt(row.countDk010)
                row.valueDk020 = trAmount(row.valueDk020)
                row.countDk020 = trInt(row.countDk020)
                // These are not currently used, so no reason to handle them
                // row.valueDkEmpty = trAmount(row.valueDkEmpty)
                // row.countDkEmpty = trInt(row.countDkEmpty)
                row.valueTotal = trAmount(row.valueTotal)
            })
            return {data: res.data, errorStatus: undefined, errorMessage: undefined}
        })
        .catch((e) => handleApiErrors<Models.EmptyingResponse>(e))
}

export async function getStatsCardPayment(type: number, params: Models.Params): Promise<ApiResponse<Models.CardPaymentData>> {
    return Axios.post<Models.CardPaymentData>(`${baseUrl()}/reports/cardtypes/${params.location}/${type}`, { ...params.body })
    .then(res => {
        // Nicer header
        res.data.result.headers.forEach((header, index) => {
            res.data.result.headers[index] = trCard(header)
        })
        // First one is always the date
        res.data.result.headers[0] = "Dato"

        res.data.result.rows.forEach(row => {
            // Handle each element in this row
            row.forEach((element, index) => {
                if (index === 0) {
                    // First is always the date
                    row[0] = trDate(row[0] as string)
                } else if (type === 0) {
                    row[index] = trAmount(row[index])
                } else {
                    row[index] = trInt(row[index])
                }
            })
        })

        if (res.data.result.otherCardHeaders) {
            // Nicer header
            res.data.result.otherCardHeaders.forEach((header, index) => {
                if (index === 0) {
                    // First one is always the date
                    res.data.result.otherCardHeaders[0] = "Dato"
                } else {
                    res.data.result.otherCardHeaders[index] = trCard(header)
                }
            })
        }

        if (res.data.result.otherCardRows) {
            res.data.result.otherCardRows.forEach(row => {
                // Handle each element in this row
                row.forEach((element, index) => {
                    if (index === 0) {
                        // First is always the date
                        row[0] = trDate(row[0] as string)
                    } else if (type === 0) {
                        row[index] = trAmount(row[index])
                    } else {
                        row[index] = trInt(row[index])
                    }
                })
            })
        }

        return {data: res.data, errorStatus: undefined, errorMessage: undefined}
    })
    .catch((e) => handleApiErrors<Models.CardPaymentData>(e))
}

export async function getStatsCardPaymentAmount(params: Models.Params): Promise<ApiResponse<Models.CardPaymentData>> {
    return getStatsCardPayment(0, params)
}

export async function getStatsCardPaymentCount(params: Models.Params): Promise<ApiResponse<Models.CardPaymentData>> {
    return getStatsCardPayment(1, params)
}

export async function getTransactionReport(type: number, params: Models.Params): Promise<ApiResponse<Models.TransactionReportResponse>> {
    return Axios.post<Models.TransactionReportResponse>(`${baseUrl()}/reports/transactions/${params.location}/${type}`, { ...params.body })
        .then(res => {
            res.data.result.data.forEach(element => {
                element.cardFirm = trCard(element.cardFirm)
                element.cardCount = trInt(Number(element.cardCount)) 
                element.amount = trAmount(element.amount)
                element.fee = trAmount(element.fee)
                element.paymentTimeString = trDateTimeSeconds(element.paymentTime)
                element.parkingTime = trInt(element.parkingTime)
                element.start = trDateTimeSeconds(element.start)
                element.end = trDateTimeSeconds(element.end)
            })

            res.data.result.sums.forEach(element => {
                element.cardFirm = trCard(element.cardFirm)
                element.cardFirm = trCard(element.cardFirm)
                element.cardCount = trInt(Number(element.cardCount)) 
                element.amount = trAmount(element.amount)
                element.fee = trAmount(element.fee)
                element.parkingTime = trInt(Number(element.parkingTime))
            })
            res.data.result.rates.forEach(element => {
                element.cardCount = trInt(Number(element.cardCount)) 
                element.parkingTime = trInt(Number(element.parkingTime))
            })
            return {data: res.data, errorStatus: undefined, errorMessage: undefined}
        })
        .catch((e) => handleApiErrors<Models.TransactionReportResponse>(e))
}

export async function getPostPaymentReport(params: Models.Params): Promise<ApiResponse<Models.PostPaymentResponse>> {
    return Axios.post<Models.PostPaymentResponse>(`${baseUrl()}/reports/postpaid/${params.location}`, { ...params.body })
        .then(res => {return {data: res.data, errorStatus: undefined, errorMessage: undefined}})
        .catch((e) => handleApiErrors<Models.PostPaymentResponse>(e))
}

export async function getAlarmsReport(params: Models.AlarmsParams): Promise<ApiResponse<Models.AlarmsResponse>> {
    return Axios.post<Models.AlarmsResponse>(`${baseUrl()}/reports/alarms_transactions/${params.location}`, { ...params.body })
        .then(res => {
            res.data.result.forEach(element => {
                element.timeString = trDateTimeSeconds(element.time)
            })
            return {data: res.data, errorStatus: undefined, errorMessage: undefined}
        })
        .catch((e) => handleApiErrors<Models.AlarmsResponse>(e))
}

export async function getLicenseList(location: number): Promise<ApiResponse<Models.LicenseResponse>> {
    return Axios.get<Models.LicenseResponse>(`${baseUrl()}/statistics/devices/${location}`)
        .then(res => { return {data: res.data, errorStatus: undefined, errorMessage: undefined}})
        .catch((e) => handleApiErrors<Models.LicenseResponse>(e))
}

export async function getDigitalLicenses(elem: any): Promise<ApiResponse<Models.DigitalLicensesResponse>> {
    return Axios.get<Models.DigitalLicensesResponse>(`${baseUrl()}/digital_licenses/requests`)
    .then(res => {

        let newRes = res.data.result.forEach(element => {
            element.licenseType = element.licenseType
            element.period = element.period
            element.count = element.count
            element.area = element.area
            element.name = element.name
            element.address = element.address
            element.startDate = element.startDate
            element.city = element.city
        })

        return {data: res.data, errorStatus: undefined, errorMessage: undefined}
    })
    .catch((e) => handleApiErrors<Models.DigitalLicensesResponse>(e))
}

export async function getDigitalLicensesStats(params: Models.DigitalLicensesStatsParams): Promise<ApiResponse<Models.DigitalLicensesStatsResponse>> {
    return Axios.post<Models.DigitalLicensesStatsResponse>(`${baseUrl()}/statistics/digital_licenses`, { ...params })
    .then(res => {
        return {data: res.data, errorStatus: undefined, errorMessage: undefined}
    })
    .catch((e) => handleApiErrors<Models.DigitalLicensesStatsResponse>(e))
}
